/**
 * Path name, weight, path settings, landers & offers (detail column).
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import type { VoluumPathUpdate } from '@trafficpulse/shared';

import { Input } from '@/components/ui/form/input/Input';
import { Switch } from '@/components/ui/form/controls/Switch';
import { cn } from '@/lib/utils';

import PathLandersOffersGrid from '../../paths/PathLandersOffersGrid';
import PathSettingsPanel from '../../paths/settings/PathSettingsPanel';
import WeightInput from '../../paths/WeightInput';

export interface PathDetailColumnProps {
  path: VoluumPathUpdate;
  weightLocked: boolean;
  onPatch: (mutator: (p: VoluumPathUpdate) => void) => void;
}

const PathDetailColumn: FC<PathDetailColumnProps> = ({
  path,
  weightLocked,
  onPatch,
}) => {
  return (
  <div className="space-y-2.5">
    <div className="space-y-1.5">
      <label className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        Path name
      </label>
      <Input
        value={path.name}
        onChange={(e) =>
          onPatch((p) => {
            p.name = e.target.value;
          })
        }
        placeholder="Path name"
        aria-label="Path name"
        className="h-8 font-semibold"
      />
      <div
        className={cn(
          'flex flex-wrap items-center gap-2 rounded border border-border/60 bg-muted/10 px-2 py-1',
        )}
      >
        <label className="flex items-center gap-1 text-[11px]">
          <span className="font-semibold uppercase tracking-wide text-muted-foreground">
            Weight
          </span>
          <WeightInput
            readOnly={weightLocked}
            readOnlyHint="Path weight is managed by Voluum (Traffic Distribution AI on for this set)"
            value={path.weight}
            onChange={(v) =>
              onPatch((p) => {
                p.weight = v;
              })
            }
            ariaLabel={`Weight ${path.name || path.id}`}
          />
        </label>
        <div className="flex items-center gap-1.5">
          <Switch
            checked={path.active}
            onCheckedChange={(v) =>
              onPatch((p) => {
                p.active = v;
              })
            }
            aria-label={`Path active ${path.name || path.id}`}
          />
          <span
            className={cn(
              'text-[10px] font-bold uppercase',
              path.active ? 'text-success' : 'text-muted-foreground',
            )}
          >
            {path.active ? 'Active' : 'Off'}
          </span>
        </div>
      </div>
    </div>
    <PathSettingsPanel path={path} onPatch={onPatch} compact />
    <PathLandersOffersGrid path={path} onPatch={onPatch} />
  </div>
  );
};

export default PathDetailColumn;
